import React from "react";
import ReactFlow, { Controls, Background, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { AccountNode } from "./AccountNode";
import { AccountsTable } from "./AccountsTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useChartOfAccountsController } from "./useChartOfAccountsController";
import { ImportButton } from "../imports/components";
import { Badge } from "@/components/ui/badge";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";

interface ChartOfAccountsProps {
  className?: string;
}

// Register custom node types
const nodeTypes = {
  accountNode: AccountNode,
};

export const ChartOfAccounts: React.FC<ChartOfAccountsProps> = ({
  className,
}) => {
  const controller = useChartOfAccountsController();
  const selectedFund = useAtomValue(SelectedFundAtom);

  return (
    <div className={`space-y-4 ${className || ""}`}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-3">
            <CardTitle className="text-2xl font-bold">
              Chart of Accounts
            </CardTitle>
            {controller.viewContext === "fund" && selectedFund && (
              <Badge variant="outline" className="text-sm">
                Filtered for {selectedFund.name}
              </Badge>
            )}
            {controller.viewContext === "client" && (
              <Badge variant="outline" className="text-sm">
                All accounts
              </Badge>
            )}
          </div>
          <div className="flex space-x-2">
            <Input
              placeholder="Search accounts..."
              value={controller.searchTerm}
              onChange={(e) => controller.setSearchTerm(e.target.value)}
              className="w-[300px]"
            />
            <ImportButton context="accounts" />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="flow" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="flow">Flow View</TabsTrigger>
              <TabsTrigger value="table">Table View</TabsTrigger>
            </TabsList>

            <TabsContent value="flow">
              <ReactFlowProvider>
                <div style={{ height: "70vh", width: "100%" }}>
                  <ReactFlow
                    nodes={controller.nodes}
                    edges={controller.edges}
                    onNodesChange={controller.onNodesChange}
                    onEdgesChange={controller.onEdgesChange}
                    onNodeDoubleClick={controller.onNodeDoubleClick}
                    nodeTypes={nodeTypes}
                    fitView
                    minZoom={0.3}
                    maxZoom={1.5}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                    proOptions={{ hideAttribution: true }}
                    defaultEdgeOptions={{
                      animated: true,
                      style: { stroke: "#888", strokeWidth: 1.5 },
                    }}
                    // nodesDraggable={false}
                    elementsSelectable={true}
                    snapToGrid={true}
                    style={{ background: "#f8fafc" }}
                    zoomOnScroll={true}
                    fitViewOptions={{
                      padding: 0.2,
                    }}
                  >
                    <Controls showInteractive={false} />
                    <Background color="#aaa" gap={16} />
                  </ReactFlow>
                </div>
              </ReactFlowProvider>
              <div className="mt-2 text-sm text-gray-500">
                Double-click on parent nodes to expand/collapse them.
              </div>
            </TabsContent>

            <TabsContent value="table">
              <AccountsTable
                accounts={controller.accounts}
                onToggleExpand={controller.toggleExpand}
                searchTerm={controller.searchTerm}
              />
              <div className="mt-2 text-sm text-gray-500">
                Click on folder icons to expand/collapse account groups.
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
